import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { financeApi } from '@/services/finance'
import { orgUsersApi, type OrgUser } from '@/services/org-users'
import { workersApi } from '@/services/workers'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { formatDateForLocale } from '@/lib/org-date-locale'
import { getUserDropdownLabel } from '@/lib/user-display'
import type { Particular, Worker } from '@/types'

type WorkerOption = {
  id: string
  label: string
}

type AddInventoryParticularDialogProps = {
  jobId: string
  jobTitle?: string | null
  workDate: string
  purchaseBy?: string
  purchaseByLocale?: string
  serviceInstanceId?: string | null
  workDayEntryId?: string | null
  particular?: Particular | null
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
  disabled?: boolean
  triggerLabel?: string
  triggerVariant?: 'default' | 'outline'
  triggerSize?: 'default' | 'sm'
  onCreated?: () => void
  onUpdated?: () => void
}

type FormState = {
  title: string
  notes: string
  quantity: string
  cost: string
  categoryId: string
  supplierId: string
  responsibleWorkerId: string
  toBePurchased: boolean
  purchaseBy: string
  payableByClient: boolean
}

const getWorkDateLabel = (value?: string | null, locale = 'en-US') => {
  if (!value?.trim()) {
    return '-'
  }

  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
    ? new Date(`${value.trim()}T12:00:00`)
    : new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const createDefaultFormState = (defaultPurchaseBy: string): FormState => ({
  title: '',
  notes: '',
  quantity: '1',
  cost: '',
  categoryId: 'none',
  supplierId: 'none',
  responsibleWorkerId: 'none',
  toBePurchased: true,
  purchaseBy: defaultPurchaseBy.slice(0, 10),
  payableByClient: false,
})

const getSupplierLabel = (supplier: OrgUser) => {
  const name = [supplier.fname, supplier.lname].filter(Boolean).join(' ').trim()
  return name || `Supplier #${supplier.user_id || supplier.id}`
}

const getWorkerLabel = (worker: Worker) =>
  getUserDropdownLabel(worker, worker.id)

const splitSupplierNotes = (rawNotes?: string | null) => {
  const trimmed = rawNotes?.trim() || ''
  const match = trimmed.match(/^Supplier: (.+?)(?:\n([\s\S]*))?$/)

  if (match) {
    return {
      supplierLabel: match[1].trim(),
      notes: (match[2] || '').trim(),
    }
  }

  return {
    supplierLabel: '',
    notes: trimmed,
  }
}

const buildFormStateFromParticular = (particular: Particular, defaultPurchaseBy: string): FormState => {
  const { notes } = splitSupplierNotes(particular.notes)

  return {
    title: particular.title?.trim() || '',
    notes,
    quantity: String(particular.quantity ?? '1'),
    cost: String(particular.cost ?? particular.unit_price ?? '0'),
    categoryId: particular.category != null ? String(particular.category) : 'none',
    supplierId: 'none',
    responsibleWorkerId: particular.responsible_worker != null ? String(particular.responsible_worker) : 'none',
    toBePurchased: Boolean(particular.to_be_purchased),
    purchaseBy: particular.purchase_by ? particular.purchase_by.slice(0, 10) : defaultPurchaseBy.slice(0, 10),
    payableByClient: Boolean(particular.payable_by_client),
  }
}

export function AddInventoryParticularDialog({
  jobId,
  jobTitle,
  workDate,
  purchaseBy,
  purchaseByLocale = 'en-US',
  serviceInstanceId,
  workDayEntryId,
  particular = null,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  disabled = false,
  triggerLabel = 'Add inventory particular',
  triggerVariant = 'default',
  triggerSize = 'sm',
  onCreated,
  onUpdated,
}: AddInventoryParticularDialogProps) {
  const queryClient = useQueryClient()
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined && onOpenChange !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? onOpenChange : setInternalOpen
  const isEditing = Boolean(particular?.id)
  const defaultPurchaseBy = (purchaseBy ?? workDate).slice(0, 10)
  const [form, setForm] = useState<FormState>(() => (
    particular ? buildFormStateFromParticular(particular, defaultPurchaseBy) : createDefaultFormState(defaultPurchaseBy)
  ))

  const workDateLabel = useMemo(
    () => getWorkDateLabel(workDate, purchaseByLocale),
    [purchaseByLocale, workDate],
  )

  const purchaseByLabel = useMemo(
    () => formatDateForLocale(form.purchaseBy || defaultPurchaseBy, purchaseByLocale, form.purchaseBy || defaultPurchaseBy),
    [defaultPurchaseBy, form.purchaseBy, purchaseByLocale],
  )

  const categoriesQuery = useQuery({
    queryKey: ['inventory-categories', 'inv'],
    queryFn: () => financeApi.getExpenseCategories({ includeInactive: true, categoryKey: 'inv' }),
    staleTime: 60_000,
    enabled: open,
  })

  const suppliersQuery = useQuery({
    queryKey: ['supplier-directory', serviceInstanceId],
    queryFn: () => orgUsersApi.listSuppliers({ serviceInstanceId: serviceInstanceId ?? undefined }),
    staleTime: 60_000,
    enabled: open,
  })

  const workersQuery = useQuery({
    queryKey: ['workers', 'inventory-particular-dialog', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({
      page_size: 500,
      active: true,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    staleTime: 60_000,
    enabled: open,
  })

  const assignableWorkerOptions = useMemo<WorkerOption[]>(() => {
    const workerMap = new Map<string, WorkerOption>()

    for (const worker of workersQuery.data?.results ?? []) {
      const workerId = String(worker.id)
      if (!workerId || workerMap.has(workerId)) {
        continue
      }

      workerMap.set(workerId, {
        id: workerId,
        label: getWorkerLabel(worker),
      })
    }

    if (particular?.responsible_worker != null) {
      const workerId = String(particular.responsible_worker)
      if (!workerMap.has(workerId)) {
        workerMap.set(workerId, {
          id: workerId,
          label: particular.responsible_worker_name || `Worker #${workerId}`,
        })
      }
    }

    return Array.from(workerMap.values()).sort((left, right) => left.label.localeCompare(right.label))
  }, [particular?.responsible_worker, particular?.responsible_worker_name, workersQuery.data?.results])

  const assignableWorkerSelectOptions = useMemo(
    () => assignableWorkerOptions.map((worker) => ({
      value: worker.id,
      label: worker.label,
      keywords: worker.id,
    })),
    [assignableWorkerOptions],
  )

  const activeCategories = useMemo(
    () => (categoriesQuery.data ?? []).filter((category) => category.is_active !== false),
    [categoriesQuery.data],
  )

  const supplierOptions = useMemo(
    () => (suppliersQuery.data ?? []).map((supplier) => ({
      value: String(supplier.id),
      label: getSupplierLabel(supplier),
      keywords: `${supplier.email || ''} ${supplier.phone || ''}`.trim(),
    })),
    [suppliersQuery.data],
  )

  const supplierLabelById = useMemo(
    () => new Map(supplierOptions.map((option) => [option.value, option.label])),
    [supplierOptions],
  )

  useEffect(() => {
    if (!open) {
      if (!particular) {
        setForm(createDefaultFormState(defaultPurchaseBy))
      }
      return
    }

    if (particular) {
      setForm(buildFormStateFromParticular(particular, defaultPurchaseBy))
      return
    }

    setForm(createDefaultFormState(defaultPurchaseBy))
  }, [defaultPurchaseBy, open, particular])

  useEffect(() => {
    if (!open || !particular) {
      return
    }

    const { supplierLabel } = splitSupplierNotes(particular.notes)
    if (!supplierLabel) {
      return
    }

    const matchedSupplier = supplierOptions.find((option) => option.label === supplierLabel)
    if (!matchedSupplier) {
      return
    }

    setForm((current) => (
      current.supplierId === matchedSupplier.value
        ? current
        : { ...current, supplierId: matchedSupplier.value }
    ))
  }, [open, particular, supplierOptions])

  const saveMutation = useMutation({
    mutationFn: () => {
      const parsedQuantity = parseFloat(form.quantity) || 1
      const parsedCost = parseFloat(form.cost) || 0

      if (!form.title.trim()) {
        throw new Error('Title is required.')
      }

      if (!Number.isFinite(parsedCost) || parsedCost < 0) {
        throw new Error('Enter a valid cost.')
      }

      if (form.toBePurchased && !form.purchaseBy.trim()) {
        throw new Error('Purchase date is required when this item needs purchase.')
      }

      if (assignableWorkerOptions.length > 0 && form.responsibleWorkerId === 'none') {
        throw new Error('Assign this inventory item to a worker.')
      }

      const supplierLabel = supplierLabelById.get(form.supplierId)
      const composedNotes = [
        form.supplierId !== 'none' && supplierLabel ? `Supplier: ${supplierLabel}` : '',
        form.notes.trim(),
      ].filter(Boolean).join('\n')

      const payload = {
        title: form.title.trim(),
        notes: composedNotes || undefined,
        quantity: parsedQuantity,
        cost: parsedCost,
        job_id: jobId,
        category: form.categoryId !== 'none' ? form.categoryId : undefined,
        responsible_worker: form.responsibleWorkerId !== 'none' ? form.responsibleWorkerId : undefined,
        work_day_entry: workDayEntryId ?? particular?.work_day_entry ?? null,
        to_be_purchased: form.toBePurchased,
        purchase_by: form.toBePurchased ? form.purchaseBy : null,
        payable_by_client: form.payableByClient,
        is_taxable: false,
      }

      if (isEditing && particular?.id) {
        return financeApi.updateParticular(String(particular.id), payload)
      }

      return financeApi.createParticular(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['particulars'] })
      queryClient.invalidateQueries({ queryKey: ['particulars', 'workdays-calendar'] })
      toast.success(isEditing ? 'Inventory particular updated' : 'Inventory particular added')
      setOpen(false)
      if (isEditing) {
        onUpdated?.()
      } else {
        onCreated?.()
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    saveMutation.mutate()
  }

  return (
    <>
      {!hideTrigger ? (
        <Button
          type="button"
          variant={triggerVariant}
          size={triggerSize}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          {triggerLabel}
        </Button>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[620px]">
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{isEditing ? 'Edit inventory particular' : 'Add inventory particular'}</DialogTitle>
              <DialogDescription>
                {isEditing
                  ? `Update this resource for ${jobTitle || `Job #${jobId}`}.`
                  : `Schedule a resource for ${jobTitle || `Job #${jobId}`}.`}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  Job: <span className="font-medium text-foreground">{jobTitle || `Job #${jobId}`}</span>
                </span>
                <span>
                  Work date: <span className="font-medium text-foreground">{workDateLabel}</span>
                </span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="inventory-particular-category">Category</Label>
                <Select
                  value={form.categoryId}
                  onValueChange={(value) => setForm((current) => ({ ...current, categoryId: value }))}
                >
                  <SelectTrigger id="inventory-particular-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {activeCategories.map((category) => (
                      <SelectItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="inventory-particular-title">Title</Label>
                <Input
                  id="inventory-particular-title"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Tools, materials, equipment, etc."
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory-particular-supplier">Supplier</Label>
              <SearchableSelect
                id="inventory-particular-supplier"
                ariaLabel="Supplier"
                value={form.supplierId === 'none' ? '' : form.supplierId}
                onValueChange={(value) => setForm((current) => ({ ...current, supplierId: value || 'none' }))}
                options={supplierOptions}
                placeholder="Select supplier"
                searchPlaceholder="Search suppliers"
                emptyText={suppliersQuery.isLoading ? 'Loading suppliers...' : 'No suppliers found.'}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="inventory-particular-cost">Cost</Label>
                <Input
                  id="inventory-particular-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost}
                  onChange={(event) => setForm((current) => ({ ...current, cost: event.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inventory-particular-quantity">Quantity</Label>
                <Input
                  id="inventory-particular-quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.quantity}
                  onChange={(event) => setForm((current) => ({ ...current, quantity: event.target.value }))}
                  required
                />
              </div>
            </div>

            {assignableWorkerOptions.length > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="inventory-particular-worker">Assign to worker</Label>
                <SearchableSelect
                  id="inventory-particular-worker"
                  ariaLabel="Assign to worker"
                  value={form.responsibleWorkerId === 'none' ? '' : form.responsibleWorkerId}
                  onValueChange={(value) => setForm((current) => ({ ...current, responsibleWorkerId: value || 'none' }))}
                  options={assignableWorkerSelectOptions}
                  placeholder="Select worker"
                  searchPlaceholder="Search workers"
                  emptyText={workersQuery.isLoading ? 'Loading workers...' : 'No workers found.'}
                />
                <p className="text-xs text-muted-foreground">
                  Any worker in your organisation can be assigned; they do not need to be on this job.
                </p>
              </div>
            ) : workersQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">Loading workers...</p>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/15 p-3">
                <Checkbox
                  id="inventory-particular-needs-purchase"
                  checked={form.toBePurchased}
                  onCheckedChange={(checked) => {
                    const next = checked === true
                    setForm((current) => ({
                      ...current,
                      toBePurchased: next,
                      purchaseBy: next ? current.purchaseBy || defaultPurchaseBy : '',
                    }))
                  }}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="inventory-particular-needs-purchase" className="cursor-pointer">
                    Needs purchase
                  </Label>
                  <p className="text-xs text-muted-foreground">Turn on when materials or supplies still need procurement.</p>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/15 p-3">
                <Checkbox
                  id="inventory-particular-payable-by-client"
                  checked={form.payableByClient}
                  onCheckedChange={(checked) => setForm((current) => ({ ...current, payableByClient: checked === true }))}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="inventory-particular-payable-by-client" className="cursor-pointer">
                    Payable by client
                  </Label>
                  <p className="text-xs text-muted-foreground">Include on job invoice when enabled.</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory-particular-purchase-by">Purchase date</Label>
              <Input
                id="inventory-particular-purchase-by"
                type="date"
                value={form.purchaseBy}
                onChange={(event) => setForm((current) => ({ ...current, purchaseBy: event.target.value }))}
                disabled={!form.toBePurchased}
                required={form.toBePurchased}
              />
              {form.toBePurchased ? (
                <p className="text-xs text-muted-foreground">
                  Required when this item needs purchase. Work date:{' '}
                  <span className="font-medium text-foreground">{workDateLabel}</span>
                  {form.purchaseBy && form.purchaseBy !== workDate.trim().slice(0, 10) ? (
                    <>
                      {' '}
                      · Purchase by:{' '}
                      <span className="font-medium text-foreground">{purchaseByLabel}</span>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="inventory-particular-notes">Notes</Label>
              <Textarea
                id="inventory-particular-notes"
                rows={3}
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Optional notes for this inventory item"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saveMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? <Spinner className="mr-2" /> : null}
                {isEditing ? 'Save changes' : 'Add particular'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
